import {uuidv4} from "@/utils/helpers";
import {getPosition} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/genogramUtils";
import {
	GenogramConnectionCategoryEnum,
	getGenogramConnectionCategoryByEdgeData,
} from "@shared/Genogram/GenogramConnectionCategoryEnum";
import {
	calculateStickyNoteTextHeight,
	STICKY_NOTE_TYPE,
	syncStickyNoteNodeData
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramStickyNoteHelper";

const HANDWRITING_NOTE_Y_GAP = 6;
const HANDWRITING_NOTE_WIDTH = 240;
const HANDWRITING_NOTE_DEFAULT_NODE_HEIGHT = 76;
const HANDWRITING_NOTE_HEIGHT = 130;
const GENERATION_GRID_SPACING = 35;
const HANDWRITING_SOCIAL_LINE_OFFSET_Y = GENERATION_GRID_SPACING * 2;
const HANDWRITING_NOTE_BG = "#FFFFFF";
const HANDWRITING_NOTE_BORDER = "#D9DEE5";
const HANDWRITING_NOTE_TOP_TOUCH_OFFSET_Y = 7;
const HANDWRITING_SHIFT_Y = HANDWRITING_SOCIAL_LINE_OFFSET_Y;
const HANDWRITING_METADATA_KEY = "handwritingMetadata";
const HANDWRITING_LABEL_FIELD = "originalLabel";
const HANDWRITING_SHIFT_FIELD = "shiftY";

export function applyHandwritingSpaceToGenogramItem(genogramItem) {
	let nodes = genogramItem?.jsonData?.elements?.nodes;
	let edges = genogramItem?.jsonData?.elements?.edges;
	if (!Array.isArray(nodes) || nodes.length === 0) {
		return genogramItem;
	}

	applyLocalHandwritingShift(nodes, edges);
	applyHandwritingOffsetToFamilyEdges(edges);
	let noteNodes = nodes
		.filter(shouldAddHandwritingSpace)
		.map((node) => {
			let noteNode = buildHandwritingNoteNode(node);
			noteNode.position.y = calculateNoteY(node, noteNode.data.height);
			return noteNode;
		});

	nodes.push(...noteNodes);
	return genogramItem;
}

export function revertHandwritingSpaceFromGenogramItem(genogramItem) {
	let nodes = genogramItem?.jsonData?.elements?.nodes;
	let edges = genogramItem?.jsonData?.elements?.edges;
	if (!Array.isArray(nodes) || nodes.length === 0) {
		return genogramItem;
	}

	let noteNodes = nodes.filter(node => node?.data?.isHandwritingSpaceNote);
	let parentNodeById = new Map(
		nodes
			.filter(node => !node?.data?.isHandwritingSpaceNote)
			.map(node => [node?.data?.id, node])
	);

	restoreHandwritingLabels(noteNodes, parentNodeById);
	restoreHandwritingShift(nodes);
	cleanupHandwritingEdgeMetadata(edges);
	removeHandwritingNodeMetadata(nodes);
	genogramItem.jsonData.elements.nodes = nodes.filter(node => !node?.data?.isHandwritingSpaceNote);
	return genogramItem;
}

export function calculateHandwritingNoteY(nodeY, nodeHeight, noteHeight) {
	return nodeY + nodeHeight / 2 + HANDWRITING_NOTE_Y_GAP + noteHeight / 2;
}

function shouldAddHandwritingSpace(node) {
	let nodeData = node?.data || {};
	let nodeType = nodeData.type || "";
	if (nodeData.isAuxNode) {
		return false
	}
	if (nodeData.isHandwritingSpaceNote) {
		return false
	}
	if (nodeType === STICKY_NOTE_TYPE) {
		return false
	}
	if (nodeType.startsWith("template")) {
		return false
	}
	return !!getPosition(node)
}

function buildHandwritingNoteNode(node) {
	let nodeData = node.data || {};
	let labelText = nodeData.label || "";
	let handwritingMetadata = ensureHandwritingMetadata(nodeData);
	if (handwritingMetadata[HANDWRITING_LABEL_FIELD] == null) {
		handwritingMetadata[HANDWRITING_LABEL_FIELD] = labelText;
	}
	nodeData.label = "";
	nodeData.isHandwritingLabelMoved = true;

	let noteNode = {
		group: "nodes",
		data: {
			id: `handwriting_${nodeData.id || uuidv4()}`,
			type: STICKY_NOTE_TYPE,
			noteText: labelText,
			width: HANDWRITING_NOTE_WIDTH,
			height: HANDWRITING_NOTE_HEIGHT,
			keepFixedHeight: true,
			bg: HANDWRITING_NOTE_BG,
			borderColor: HANDWRITING_NOTE_BORDER,
			opacity: 1,
			border: true,
			textVAlign: "center",
			textMarginY: calculateHandwritingNoteTextMarginY(labelText, HANDWRITING_NOTE_HEIGHT),
			skipSnapToGrid: true,
			isHandwritingSpaceNote: true,
			parentNodeId: nodeData.id || null,
		},
		position: {
			x: getPosition(node).x,
			y: getPosition(node).y,
		},
		grabbable: false,
	};
	syncStickyNoteNodeData(noteNode.data);
	return noteNode;
}

function applyHandwritingOffsetToFamilyEdges(edges = []) {
	if (!Array.isArray(edges)) {
		return
	}

	edges.forEach((edge) => {
		let edgeData = edge?.data || {};
		if (getGenogramConnectionCategoryByEdgeData(edgeData, null) !== GenogramConnectionCategoryEnum.SOCIAL) {
			return
		}
		let handwritingMetadata = ensureHandwritingMetadata(edgeData);
		handwritingMetadata[HANDWRITING_SHIFT_FIELD] = HANDWRITING_SOCIAL_LINE_OFFSET_Y;
		edgeData.familyLineOffsetY = handwritingMetadata[HANDWRITING_SHIFT_FIELD];
	});
}

function applyLocalHandwritingShift(nodes = [], edges = []) {
	let originalNodes = nodes.filter(node => !node?.data?.isHandwritingSpaceNote);
	let nodeById = new Map(originalNodes.map(node => [node?.data?.id, node]));

	resetHandwritingShiftFlags(originalNodes);
	markNodesWithSocialConnection(edges, nodeById);
	applyHandwritingShiftFlags(originalNodes);
}

function resetHandwritingShiftFlags(nodes = []) {
	nodes.forEach((node) => {
		node.data.needShiftUpForHandwriting = false;
	});
}

function markNodesWithSocialConnection(edges = [], nodeById = new Map()) {
	if (!Array.isArray(edges)) {
		return
	}

	edges.forEach((edge) => {
		let edgeData = edge?.data || {};
		if (getGenogramConnectionCategoryByEdgeData(edgeData, null) !== GenogramConnectionCategoryEnum.SOCIAL) {
			return
		}

		let sourceNode = nodeById.get(edgeData.source);
		let targetNode = nodeById.get(edgeData.target);
		markNodeForHandwritingShift(sourceNode);
		markNodeForHandwritingShift(targetNode);
	});
}

function markNodeForHandwritingShift(node) {
	if (!node?.data || node.data.isAuxNode) {
		return
	}

	node.data.needShiftUpForHandwriting = true;
}

function applyHandwritingShiftFlags(nodes = []) {
	nodes.forEach((node) => {
		let position = getPosition(node);
		if (!position) {
			return
		}

		if (!node?.data?.needShiftUpForHandwriting) {
			return
		}

		let handwritingMetadata = ensureHandwritingMetadata(node.data);
		handwritingMetadata[HANDWRITING_SHIFT_FIELD] = HANDWRITING_SHIFT_Y;
		position.y -= handwritingMetadata[HANDWRITING_SHIFT_FIELD];
	});
}

function restoreHandwritingLabels(noteNodes = [], parentNodeById = new Map()) {
	noteNodes.forEach((noteNode) => {
		let parentNodeId = noteNode?.data?.parentNodeId;
		let parentNode = parentNodeById.get(parentNodeId);
		if (!parentNode?.data) {
			return
		}

		let handwritingMetadata = parentNode.data[HANDWRITING_METADATA_KEY];
		if (!handwritingMetadata || handwritingMetadata[HANDWRITING_LABEL_FIELD] == null) {
			return
		}

		parentNode.data.label = handwritingMetadata[HANDWRITING_LABEL_FIELD];
		parentNode.data.isHandwritingLabelMoved = false;
	});
}

function restoreHandwritingShift(nodes = []) {
	nodes.forEach((node) => {
		if (node?.data?.isHandwritingSpaceNote) {
			return
		}

		let position = getPosition(node);
		let shiftY = Number(node?.data?.[HANDWRITING_METADATA_KEY]?.[HANDWRITING_SHIFT_FIELD]) || 0;
		if (!position || !shiftY) {
			return
		}

		position.y += shiftY;
	});
}

function cleanupHandwritingEdgeMetadata(edges = []) {
	if (!Array.isArray(edges)) {
		return
	}

	edges.forEach((edge) => {
		let edgeData = edge?.data || {};
		if ("familyLineOffsetY" in edgeData) {
			delete edgeData.familyLineOffsetY;
		}
		delete edgeData[HANDWRITING_METADATA_KEY];
	});
}

function removeHandwritingNodeMetadata(nodes = []) {
	nodes.forEach((node) => {
		let nodeData = node?.data || {};
		delete nodeData.needShiftUpForHandwriting;
		delete nodeData[HANDWRITING_METADATA_KEY];
		delete nodeData.isHandwritingLabelMoved;
	});
}

function ensureHandwritingMetadata(data = {}) {
	if (!data[HANDWRITING_METADATA_KEY]) {
		data[HANDWRITING_METADATA_KEY] = {};
	}

	return data[HANDWRITING_METADATA_KEY];
}

function calculateNoteY(node, noteHeight) {
	let nodePosition = getPosition(node);
	let nodeHeight = Number(node?.data?.height) || HANDWRITING_NOTE_DEFAULT_NODE_HEIGHT;
	return calculateHandwritingNoteY(nodePosition.y, nodeHeight, noteHeight);
}

export function calculateHandwritingNoteTextMarginY(text, noteHeight) {
	let textHeight = calculateStickyNoteTextHeight(text, HANDWRITING_NOTE_WIDTH);
	let res = Math.round(
		-HANDWRITING_NOTE_HEIGHT / 2 +
		textHeight / 2 +
		HANDWRITING_NOTE_TOP_TOUCH_OFFSET_Y
	);
	console.log("calculateHandwritingNoteTextMarginY: noteHeight = ", noteHeight, ", textHeight = ", textHeight, ", text = ", text, ", offset = ", res)
	return res
}
